import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Check,
  ChevronDown,
  Clock,
  CupSoda,
  ListTodo,
  Pause,
  Play,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { Button } from '@/components/Button';
import { Collapse } from '@/components/Collapse';
import { EventLocation } from '@/features/calendars/EventLocation';
import type { PlanEvent } from '@/features/calendars/PlanEventSheet';
import type { TimeBlock } from '@/features/calendars/types';
import { useTimeBlocks } from '@/features/calendars/useTimeBlocks';
import { useBlockers } from '@/features/calendars/useBlockers';
import { useToggleEventDone } from '@/features/calendars/useCalendarEvents';
import { useCompletionNote } from '@/features/tasks/CompletionNote';
import { useClients, useTasks, useToggleDone, useUpdateTask } from '@/features/tasks/useTasks';
import { formatDuration } from '@/features/tasks/tree';
import { useFocus, useFocusControls } from '@/features/time/useFocus';
import { formatClock, useElapsed } from '@/features/time/useTimer';
import { buildTodayAgenda, isCompletedTodayTask, isDueTodayTask, type AgendaItem } from './todayAgenda';
import { todayRange } from './greeting';

interface Props {
  timezone: string;
  onOpenTask: (taskId: number) => void;
  onOpenEvent: (event: PlanEvent) => void;
  onOpenBlock: (block: TimeBlock) => void;
  onOpenPlan?: () => void;
  loading?: boolean;
}

export function TodayAgendaList({
  timezone,
  onOpenTask,
  onOpenEvent,
  onOpenBlock,
  onOpenPlan,
  loading: loadingProp,
}: Props) {
  const range = useMemo(() => todayRange(timezone), [timezone]);
  const blocks = useTimeBlocks(range);
  const blockers = useBlockers(range);
  const clients = useClients();
  const overdue = useTasks({ overdue_only: true });
  // Closed tasks must be loaded: completed agenda rows need them to unmark.
  const allTasks = useTasks({ include_closed: true });
  const focus = useFocus();
  const controls = useFocusControls();
  const toggleDone = useToggleDone();
  const updateTask = useUpdateTask();
  const askForNote = useCompletionNote();
  const liveElapsed = useElapsed(focus.data?.task_started_at);
  const timerElapsedSeconds = Math.max(
    liveElapsed,
    focus.data?.task_elapsed_seconds ?? 0,
  );

  const events = useQuery({
    queryKey: ['calendars', 'events', range.start.toISOString(), range.end.toISOString()],
    queryFn: () =>
      api<PlanEvent[]>(
        `/api/calendars/events?ends_after=${encodeURIComponent(range.start.toISOString())}` +
          `&starts_before=${encodeURIComponent(range.end.toISOString())}`,
      ),
  });

  const dueTodayTasks = useMemo(
    () => (allTasks.data ?? []).filter((t) => isDueTodayTask(t, timezone)),
    [allTasks.data, timezone],
  );

  const completedTodayTasks = useMemo(
    () => (allTasks.data ?? []).filter((t) => isCompletedTodayTask(t, timezone)),
    [allTasks.data, timezone],
  );

  const items = useMemo(
    () =>
      buildTodayAgenda({
        blocks: blocks.data ?? [],
        events: events.data ?? [],
        blockers: blockers.data ?? [],
        overdueTasks: overdue.data ?? [],
        dueTodayTasks,
        completedTodayTasks,
        timezone,
        activeTaskId: focus.data?.active_task_id,
      }),
    [
      blocks.data,
      events.data,
      blockers.data,
      overdue.data,
      dueTodayTasks,
      completedTodayTasks,
      timezone,
      focus.data?.active_task_id,
    ],
  );

  const toggleEventDone = useToggleEventDone();
  const [showCompleted, setShowCompleted] = useState(false);

  const { openItems, completedItems } = useMemo(() => {
    const open: AgendaItem[] = [];
    const completed: AgendaItem[] = [];
    for (const item of items) {
      if (item.done) completed.push(item);
      else open.push(item);
    }
    return { openItems: open, completedItems: completed };
  }, [items]);

  const colorFor = (clientId: number) =>
    clients.data?.find((c) => c.id === clientId)?.color ?? 'var(--border)';

  const loading =
    loadingProp ||
    ((blocks.isPending && !blocks.data) ||
      (events.isPending && !events.data) ||
      (allTasks.isPending && !allTasks.data));

  const showHardError =
    (blocks.isError && !blocks.data) ||
    (events.isError && !events.data) ||
    (allTasks.isError && !allTasks.data);

  const retryAgenda = () => {
    void blocks.refetch();
    void events.refetch();
    void allTasks.refetch();
  };

  const renderRow = (item: AgendaItem, nowLineIndex: number, index: number) => (
    <li key={item.key}>
      {index === nowLineIndex && index > 0 && (
        <div className="relative py-1" aria-hidden>
          <div className="absolute inset-x-3 top-1/2 h-px bg-accent/40" />
          <span className="relative mx-3 inline-block bg-surface-1 px-2 text-label text-accent">
            Up next
          </span>
        </div>
      )}
      <AgendaRow
        item={item}
        clientColor={item.task ? colorFor(item.task.client_id) : item.color}
        isTiming={
          item.block
            ? focus.data?.active_task_id === item.block.task_id &&
              focus.data.mode === 'working'
            : item.event
              ? focus.data?.active_calendar_event_id === item.event.id
              : item.task
                ? focus.data?.active_task_id === item.task.id &&
                  focus.data.mode === 'working'
                : false
        }
        timerElapsedSeconds={timerElapsedSeconds}
        onOpen={() => {
          if (item.block) onOpenBlock(item.block);
          else if (item.event) onOpenEvent(item.event);
          else if (item.task) onOpenTask(item.task.id);
          // Blockers are edited where they are placed.
          else if (item.blocker) onOpenPlan?.();
        }}
        onToggleDone={() => {
          if (item.event) {
            toggleEventDone.mutate({
              eventId: item.event.id,
              done: !item.done,
            });
            return;
          }
          const taskId = item.block?.task_id ?? item.task?.id;
          if (!taskId) return;
          const task = item.task ?? allTasks.data?.find((t) => t.id === taskId);
          if (task) {
            toggleDone(task);
            return;
          }
          // Task list can lag; drive status from the agenda row's done flag.
          updateTask.mutate({
            id: taskId,
            status: item.done ? 'backlog' : 'done',
          });
          if (!item.done) askForNote(taskId, item.title ?? null);
        }}
        onStartFocus={() => {
          if (item.event) {
            controls.startEvent.mutate({ calendar_event_id: item.event.id });
            return;
          }
          const taskId = item.block?.task_id ?? item.task?.id;
          if (taskId) controls.start.mutate({ task_id: taskId });
        }}
        onPauseFocus={() => {
          if (item.event) {
            controls.pauseTask.mutate();
            return;
          }
          controls.pause.mutate();
        }}
      />
    </li>
  );

  if (loading) {
    return (
      <ul className="space-y-2 px-2" aria-hidden>
        {[0, 1, 2, 3].map((i) => (
          <li key={i} className="h-14 animate-pulse rounded bg-surface-2" />
        ))}
      </ul>
    );
  }

  if (showHardError) {
    return (
      <p role="alert" className="px-4 py-6 text-body text-danger">
        Could not load today&apos;s agenda.{' '}
        <button type="button" className="underline" onClick={retryAgenda}>
          Retry
        </button>
      </p>
    );
  }

  if (items.length === 0) {
    return (
      <div className="px-4 py-8 text-center">
        <p className="text-body text-text-secondary">Nothing scheduled for today yet.</p>
        <p className="mt-1 text-small text-text-tertiary">
          Capture a task above, drag work onto the Plan calendar, or wait for appointments to sync
          in.
        </p>
        {onOpenPlan && (
          <Button variant="secondary" onClick={onOpenPlan} className="mt-4">
            Open Plan
          </Button>
        )}
      </div>
    );
  }

  const nowLineIndex = openItems.findIndex(
    (item) => !item.past && item.kind !== 'task',
  );

  return (
    <div>
      {openItems.length === 0 ? (
        <div className="px-4 py-6 text-center">
          <p className="text-body text-text-secondary">Agenda clear. Nice work.</p>
          <p className="mt-1 text-small text-text-tertiary">
            Completed items are tucked away below when you want them.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-border" aria-label="Today's agenda">
          {openItems.map((item, index) => renderRow(item, nowLineIndex, index))}
        </ul>
      )}

      {completedItems.length > 0 && (
        <div className="border-t border-border">
          <button
            type="button"
            onClick={() => setShowCompleted((v) => !v)}
            aria-expanded={showCompleted}
            className={cn(
              'flex w-full items-center gap-2 px-3 py-2.5 text-left',
              'text-small font-medium text-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-ring',
              'hover:bg-surface-2 hover:text-text',
            )}
          >
            <span className="flex-1 tabular">
              {showCompleted ? 'Hide' : 'Show'} {completedItems.length} completed
            </span>
            <ChevronDown
              size={14}
              strokeWidth={1.5}
              aria-hidden
              className={cn(
                'transition-transform duration-press',
                showCompleted && 'rotate-180',
              )}
            />
          </button>
          <Collapse open={showCompleted}>
            <ul
              className="divide-y divide-border border-t border-border"
              aria-label="Completed agenda items"
            >
              {completedItems.map((item) => renderRow(item, -1, -1))}
            </ul>
          </Collapse>
        </div>
      )}
    </div>
  );
}

function AgendaRow({
  item,
  clientColor,
  isTiming,
  timerElapsedSeconds,
  onOpen,
  onToggleDone,
  onStartFocus,
  onPauseFocus,
}: {
  item: AgendaItem;
  clientColor: string;
  isTiming: boolean;
  timerElapsedSeconds: number;
  onOpen: () => void;
  onToggleDone: () => void;
  onStartFocus: () => void;
  onPauseFocus: () => void;
}) {
  const blocker = item.kind === 'blocker';

  const scheduledMinutes =
    item.event && !item.event.all_day
      ? Math.max(
          1,
          Math.round(
            (new Date(item.event.ends_at).getTime() -
              new Date(item.event.starts_at).getTime()) /
              60_000,
          ),
        )
      : null;

  const kindIcon = blocker ? (
    <CupSoda size={13} strokeWidth={1.5} aria-hidden />
  ) : item.kind === 'event' ? (
    <Calendar size={13} strokeWidth={1.5} aria-hidden />
  ) : item.kind === 'block' ? (
    <Clock size={13} strokeWidth={1.5} aria-hidden />
  ) : (
    <ListTodo size={13} strokeWidth={1.5} aria-hidden />
  );

  const kindLabel = blocker
    ? 'Blocker'
    : item.kind === 'event'
      ? 'Appointment'
      : item.kind === 'block'
        ? 'Scheduled task'
        : 'Task';

  return (
    <div
      className={cn(
        'group flex items-start gap-2 rounded px-2 py-3 transition-colors duration-press',
        item.active && 'bg-accent-quiet/60',
        !item.active && 'hover:bg-surface-2',
      )}
    >
      {blocker ? (
        // Nothing to tick off: a blocker is time passing, not work finishing.
        <span
          aria-hidden
          className="mt-0.5 flex size-check shrink-0 items-center justify-center rounded-sm border border-dashed border-border"
        />
      ) : (
        <button
          type="button"
          onClick={onToggleDone}
          aria-pressed={item.done}
          aria-label={item.done ? `Mark "${item.title}" not done` : `Complete "${item.title}"`}
          className={cn(
            'pressable mt-0.5 flex size-check shrink-0 items-center justify-center rounded-sm border',
            item.done
              ? 'border-accent bg-accent text-text-on-accent'
              : 'border-border bg-surface-1 hover:border-accent-border',
          )}
        >
          {item.done && <Check size={12} strokeWidth={2} aria-hidden />}
        </button>
      )}

      <div className="min-w-0 flex-1">
        <button type="button" onClick={onOpen} className="pressable w-full text-left">
          <div className="flex items-center gap-2">
            <span
              className="size-1.5 shrink-0 rounded-full"
              style={{ backgroundColor: clientColor }}
              aria-hidden
            />
            <span
              className={cn(
                'min-w-0 flex-1 truncate text-body font-medium',
                blocker && 'font-normal italic text-text-secondary',
                item.done && 'text-text-tertiary line-through',
              )}
            >
              {item.title}
            </span>
          </div>

          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 pl-3.5 text-small text-text-tertiary">
            <span className="inline-flex items-center gap-1 tabular">
              {kindIcon}
              <span>{kindLabel}</span>
              <span aria-hidden>·</span>
              <span>{item.timeLabel}</span>
            </span>

            {item.block?.estimate_minutes != null && item.block.estimate_minutes > 0 && (
              <span className="tabular">{formatDuration(item.block.estimate_minutes)}</span>
            )}

            {isTiming && (
              <span className="tabular text-accent">
                {formatClock(timerElapsedSeconds)}
                {scheduledMinutes != null && (
                  <> / {formatDuration(scheduledMinutes)}</>
                )}
              </span>
            )}

            {item.past && !item.done && !blocker && (
              <span className="text-warning">Needs attention</span>
            )}
          </div>
        </button>

        {item.event?.location && (
          <div className="mt-1 pl-3.5 text-small text-text-tertiary">
            <EventLocation location={item.event.location} compact />
          </div>
        )}
      </div>

      {!blocker && !item.done && (item.kind !== 'event' || !item.event?.all_day) && (
        <button
          type="button"
          onClick={() => (isTiming ? onPauseFocus() : onStartFocus())}
          aria-label={
            isTiming
              ? `Pause timer on "${item.title}"`
              : item.kind === 'event'
                ? `Start meeting timer on "${item.title}"`
                : `Start focus on "${item.title}"`
          }
          className={cn(
            'pressable mt-0.5 flex size-control shrink-0 items-center justify-center rounded-full',
            isTiming
              ? 'bg-accent text-text-on-accent'
              : 'text-text-tertiary opacity-0 hover:bg-surface-3 hover:text-text group-hover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
          )}
        >
          {isTiming ? (
            <Pause size={13} strokeWidth={1.5} aria-hidden />
          ) : (
            <Play size={13} strokeWidth={1.5} aria-hidden />
          )}
        </button>
      )}
    </div>
  );
}
